import { faEdit, faProjectDiagram, faTrashAlt, faUndoAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { ButtonGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { AssignDialogProps } from '../../components/AssignDialog'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FlipCard } from '../../components/FlipCard'
import ProtectedButton from '../../components/ProtectedButton'
import { SelectDialogProps } from '../../components/SelectDialog'
import Loading from '../../layout/Loading'
import AdminService from '../../service/AdminService'
import { formatBooleanColumn } from '../../utils/DatatableUtil'

const Administrator: FC<{}> = () => {
  const { t } = useTranslation()
  const [admins, setAdmins] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const context = useContext(DialogContext)

  useEffect(() => {
    AdminService.getAdmins(setAdmins).finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    const editOrAssign = [...Authorization.userManagement.administration.editAdmin, ...Authorization.userManagement.administration.assignRole]
    const { confirmDialogPropsSetter, editDialogPropsSetter, assignDialogPropsSetter } = context
    return [
      {
        text: t('username.text'),
        dataField: 'username',
        sort: true,
        classes: 'position-relative',
        formatter: (cellData: any, row: any) => {
          return AuthService.hasPermission(userPermissions, Authorization.userManagement.administration.editAdmin) ? (
            <ProtectedButton
              variant='link'
              className='text-decoration-none pl-0 stretched-link text-left'
              tooltip={t('edit')}
              onClick={() => editAdminDialog(t, editDialogPropsSetter, setAdmins, row)}>
              <FontAwesomeIcon icon={faEdit} /> {cellData}
            </ProtectedButton>
          ) : (
            cellData
          )
        },
      },
      {
        text: t('firstname.text'),
        dataField: 'firstname',
        sort: true,
      },
      {
        text: t('lastname.text'),
        dataField: 'lastname',
        sort: true,
      },
      {
        text: t('email.text'),
        dataField: 'mail',
        sort: true,
      },
      {
        text: t('active'),
        dataField: 'active',
        formatter: (cell: any) => formatBooleanColumn(cell),
        sort: true,
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, editOrAssign),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.userManagement.administration.assignRole}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('admin.assignRoles')}
                onClick={() => assignRolesDialog(t, assignDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faProjectDiagram} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.userManagement.administration.editAdmin}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('admin.resetHeader')}
                onClick={() => resetAdmin(t, confirmDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faUndoAlt} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.userManagement.administration.editAdmin}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deleteAdminDialog(t, confirmDialogPropsSetter, setAdmins, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, userPermissions, context])

  const defaultSorted = [
    {
      dataField: 'username',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={admins}
        columns={columns}
        caption={t('Administrators')}
        helpLink={t('admin.helpLink')}
        captionButtons={[
          {
            onClick: () => createAdminDialog(t, context.editDialogPropsSetter, setAdmins),
            text: t('new'),
            permissions: Authorization.userManagement.administration.editAdmin,
          },
        ]}
        searchPlaceholder='Search Administrators'
        defaultSorted={defaultSorted}
      />
    </Loading>
  )
}

function createAdminDialog(t: TFunction, editDialogPropsSetter: any, setAdmins: React.Dispatch<any[]>) {
  const generator = require('generate-password')
  const password = generator.generate({
    length: 12,
    numbers: true,
    excludeSimilarCharacters: true,
    strict: true,
  })

  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('admin.createHeader'),
    onSubmit: AdminService.createAdmin,
    onSubmitExtraArgs: [t, setAdmins],
    data: [
      {
        id: 'firstname',
        type: 'text',
        label: t('firstname.text'),
        placeholder: t('firstname.text'),
        required: false,
      },
      {
        id: 'lastname',
        type: 'text',
        label: t('lastname.text'),
        placeholder: t('lastname.text'),
        required: false,
      },
      {
        id: 'username',
        type: 'text',
        label: t('username.text'),
        placeholder: t('username.text'),
        autoComplete: 'username',
        requiredMessage: t('username.inValidMessage'),
        required: true,
      },
      {
        id: 'password',
        value: password,
        type: 'hidden',
        render: false,
      },
      {
        id: 'mail',
        type: 'email',
        label: t('email.text'),
        placeholder: t('email.text'),
        required: false,
      },
      {
        groupLabel: t('active'),
        groupName: 'active',
        type: 'switch',
        checkboxes: [
          {
            id: 'active',
          },
        ],
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function editAdminDialog(t: TFunction, editDialogPropsSetter: any, setAdmins: React.Dispatch<React.SetStateAction<any[]>>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('admin.editHeader'),
    onSubmit: AdminService.updateAdmin,
    onSubmitExtraArgs: [t, setAdmins],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'firstname',
        value: row.firstname,
        type: 'text',
        label: t('firstname.text'),
        placeholder: t('firstname.text'),
        required: false,
      },
      {
        id: 'lastname',
        value: row.lastname,
        type: 'text',
        label: t('lastname.text'),
        placeholder: t('lastname.text'),
        required: false,
      },
      {
        id: 'username',
        value: row.username,
        type: 'text',
        label: t('username.text'),
        placeholder: t('username.text'),
        autoComplete: 'username',
        requiredMessage: t('username.inValidMessage'),
        plaintext: true,
        readOnly: true,
      },
      {
        id: 'mail',
        value: row.mail,
        type: 'email',
        label: t('email.text'),
        placeholder: t('email.text'),
        required: false,
      },
      {
        groupLabel: t('active'),
        groupName: 'active',
        type: 'switch',
        checkboxes: [
          {
            id: 'active',
            value: row.active,
          },
        ],
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function resetAdmin(t: TFunction, confirmDialogPropsSetter: any, row: any) {
  const generator = require('generate-password')
  const password = generator.generate({
    length: 12,
    numbers: true,
    excludeSimilarCharacters: true,
    strict: true,
  })
  row.password = password
  const resetDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('admin.resetHeader'),
    body: t('admin.resetAdminBody', { name: row.username }),
    submitText: t('yes'),
    onSubmit: AdminService.resetAdmin,
    onSubmitExtraArgs: [row, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(resetDialogProps)
}

function deleteAdminDialog(t: TFunction, confirmDialogPropsSetter: any, setAdmins: any, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('admin.deleteHeader'),
    body: t('admin.deleteBody', { name: row.username }),
    submitText: t('yes'),
    onSubmit: AdminService.deleteAdmin,
    onSubmitExtraArgs: [row, t, setAdmins],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function assignRolesDialog(t: TFunction, assignDialogPropsSetter: any, row: any) {
  AdminService.getRolesWithAssigned(row.id).then((roles) => {
    const assignDialogProps: AssignDialogProps = {
      show: true,
      header: t('admin.assignHeader', { name: row.username }),
      onSubmit: AdminService.assignRoles,
      onSubmitExtraArgs: [row.id, row.username, t],
      data: roles,
      columns: [
        {
          text: t('name.text'),
          dataField: 'name',
          sort: true,
        },
        {
          text: t('description.text'),
          dataField: 'description',
          sort: true,
        },
        {
          dataField: 'isAssigned',
          text: t('assign'),
          isAssignField: true,
        },
      ],
    }

    assignDialogPropsSetter(assignDialogProps)
  })
}

function selectAdminDialog(t: TFunction, selectDialogPropsSetter: any, admins: any[], onSubmit: any, onSubmitExtraArgs?: any[]) {
  const selectDialogProps: SelectDialogProps = {
    show: true,
    header: t('admin.selectHeader'),
    data: admins,
    keyField: 'username',
    label: t('admin.selectHeader'),
    submitText: t('select'),
    onSubmit,
    onSubmitExtraArgs,
    cancelText: t('cancel'),
  }
  selectDialogPropsSetter(selectDialogProps)
}

export const AdministratorFlipCard = () => {
  const [admins, setAdmins] = useState([] as any[])
  const { t } = useTranslation()
  const { userPermissions } = useContext(AppContext)
  const { selectDialogPropsSetter, editDialogPropsSetter, confirmDialogPropsSetter, assignDialogPropsSetter } = useContext(DialogContext)

  const viewAdmin = Authorization.userManagement.administration.viewAdmin
  const editAdmin = Authorization.userManagement.administration.editAdmin

  useEffect(() => {
    if (AuthService.hasPermission(userPermissions, viewAdmin)) {
      AdminService.getAdmins(setAdmins)
    }
  }, [userPermissions, viewAdmin])

  function handleclick(onSubmit: any, onSubmitExtraArgs?: any[] | undefined) {
    selectAdminDialog(t, selectDialogPropsSetter, admins, onSubmit, onSubmitExtraArgs)
  }

  return (
    <FlipCard
      permissions={viewAdmin}
      headerText={t('admin.text')}
      headerLink='./userManagement/administrator'
      flipFrontContent={
        <ButtonGroup vertical>
          <ProtectedButton permissions={editAdmin} variant='link' className='m-1' onClick={() => createAdminDialog(t, editDialogPropsSetter, setAdmins)}>
            {t('admin.createHeader')}
          </ProtectedButton>
          <ProtectedButton
            permissions={editAdmin}
            variant='link'
            className='m-1'
            onClick={() => handleclick(editAdminDialog, [t, editDialogPropsSetter, setAdmins])}>
            {t('admin.editHeader')}
          </ProtectedButton>
          <ProtectedButton permissions={editAdmin} variant='link' className='m-1' onClick={() => handleclick(assignRolesDialog, [t, assignDialogPropsSetter])}>
            {t('admin.assignRoles')}
          </ProtectedButton>
          <ProtectedButton permissions={editAdmin} variant='link' className='m-1' onClick={() => handleclick(resetAdmin, [t, confirmDialogPropsSetter])}>
            {t('admin.resetHeader')}
          </ProtectedButton>
          <ProtectedButton
            permissions={editAdmin}
            variant='link'
            className='m-1'
            onClick={() => handleclick(deleteAdminDialog, [t, confirmDialogPropsSetter, setAdmins])}>
            {t('admin.deleteHeader')}
          </ProtectedButton>
        </ButtonGroup>
      }
    />
  )
}

export default Administrator
